import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { OrderPhotosGallery, FilesList } from '@/components/PhotosFiles';
import { OrderPaymentWidget } from '@/components/OrderPaymentWidget';
import { OrderExpenses } from '@/components/OrderExpenses';
import { ReminderButton } from '@/components/SendReminderDialog';
import { OrderTimeline } from '@/components/orders/OrderTimeline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Briefcase,
  Phone, 
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  User,
  CheckCircle,
  XCircle,
  Play,
  Clock,
  Navigation,
  CalendarClock,
  Wrench,
  ExternalLink,
} from 'lucide-react';
import { cn, parseScheduledDate } from '@/lib/utils';
import { OPERATIONAL_STATUSES, PAYMENT_STATUSES, PRIORITIES } from './constants';

export function OrderDetailSheet({ order: orderProp, open, onOpenChange, onRefresh, onSchedule, onCreateService, onViewService }) {
  const { api, hasRole } = useAuth();
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [order, setOrder] = useState(orderProp);

  // Fetch full order details when sheet opens
  useEffect(() => {
    if (open && orderProp?.id) {
      setOrder(orderProp); // show list data immediately
      api.get(`/orders/${orderProp.id}`).then(res => {
        setOrder(res.data);
      }).catch(() => {});
    }
  }, [open, orderProp?.id, api]);
  
  if (!order) return null;

  const handleStatusChange = async (statusType, newStatus) => {
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/actions/change-${statusType}-status`, {
        new_status: newStatus
      });
      onRefresh();
    } catch (err) {
      console.error('Failed to change status:', err);
    } finally {
      setLoading(false);
    }
  };

  const opStatus = OPERATIONAL_STATUSES[order.operational_status];
  const payStatus = PAYMENT_STATUSES[order.payment_status];
  const StatusIcon = opStatus?.icon || Clock;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            {order.title}
          </SheetTitle>
          <SheetDescription>
            {order.order_type === 'service' ? 'Сервисный выезд — бесплатно для клиента' : `${t('orders.orderTotal')}: ${order.total_amount?.toLocaleString()} ${t('currency.symbol')}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Statuses */}
          <div className="flex flex-wrap gap-2">
            <Badge className={cn("flex items-center gap-1", opStatus?.color)}>
              <StatusIcon className="h-3 w-3" />
              {t(opStatus?.labelKey)}
            </Badge>
            {order.order_type !== 'service' && (
              <Badge className={payStatus?.color}>
                {t(payStatus?.labelKey)}
              </Badge>
            )}
            {order.order_type === 'service' && (
              <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">Сервис</Badge>
            )}
          </div>

          {/* Client Info */}
          {order.client && (
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium">{order.client.name}</p>
                  <div className="flex gap-3 text-sm text-muted-foreground">
                    {order.client.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {order.client.phone}
                      </span>
                    )}
                    {order.client.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {order.client.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Schedule */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Расписание</h4>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="font-medium">
                  {order.scheduled_date 
                    ? parseScheduledDate(order.scheduled_date)?.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })
                    : t('orders.noDate')}
                </p>
                {order.scheduled_time_start && (
                  <p className="text-sm text-muted-foreground">
                    {order.scheduled_time_start} — {order.scheduled_time_end || '?'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          {(order.address_street || order.address_string) && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-accent" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{order.address_street || order.address_string}</p>
                <p className="text-xs text-muted-foreground">Адрес выполнения</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => {
                  const address = encodeURIComponent(order.address_street || order.address_string);
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
                }}
                data-testid="navigate-to-address"
              >
                <Navigation className="h-4 w-4 mr-1" />
                Навигация
              </Button>
            </div>
          )}

          {/* Assigned Workers */}
          {order.assigned_workers && order.assigned_workers.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Назначенные работники</h4>
              <div className="flex flex-wrap gap-2">
                {order.assigned_workers.map(worker => (
                  <div key={worker.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: worker.color }}
                    />
                    <span className="text-sm">{worker.first_name} {worker.last_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price — hide for service orders (free for client) */}
          {order.order_type !== 'service' && (
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Сумма заказа</span>
                <span className="text-2xl font-bold tabular-nums">
                  {t('currency.format', order.total_amount || 0)}
                </span>
              </div>
              {order.order_expenses > 0 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground border-t pt-2">
                  <span>Расходы (одобренные)</span>
                  <span className="text-amber-500">−{t('currency.format', order.order_expenses)}</span>
                </div>
              )}
            </div>
          )}

          {/* Payment Widget — hide for service orders */}
          {order.operational_status !== 'cancelled' && order.order_type !== 'service' && (
            <OrderPaymentWidget order={order} onPaymentComplete={onRefresh} />
          )}

          {/* Actions */}
          {(hasRole('admin') || hasRole('manager')) && (
            <div className="space-y-3 pt-4 border-t">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Действия</h4>
              
              {/* Schedule button for new/unscheduled orders */}
              {order.operational_status === 'new' && (
                <Button 
                  size="sm"
                  variant="default"
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={() => {
                    onOpenChange(false);
                    onSchedule?.(order);
                  }}
                  data-testid="order-schedule-btn"
                >
                  <CalendarClock className="mr-2 h-4 w-4" />
                  Запланировать
                </Button>
              )}

              {/* Operational Status Actions — hide confirm/cancel for service orders */}
              <div className="flex flex-wrap gap-2">
                {order.operational_status === 'new' && order.order_type !== 'service' && (
                  <>
                    <Button 
                      size="sm" 
                      onClick={() => handleStatusChange('operational', 'confirmed')}
                      disabled={loading}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Подтвердить
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleStatusChange('operational', 'cancelled')}
                      disabled={loading}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Отменить
                    </Button>
                  </>
                )}
                {order.operational_status === 'confirmed' && (
                  <Button 
                    size="sm" 
                    onClick={() => handleStatusChange('operational', 'in_progress')}
                    disabled={loading}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Начать работу
                  </Button>
                )}
                {order.operational_status === 'in_progress' && (
                  <Button 
                    size="sm" 
                    onClick={() => handleStatusChange('operational', 'done')}
                    disabled={loading}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Завершить
                  </Button>
                )}
              </div>

              {/* Payment Status Actions — hide for service orders */}
              {order.order_type !== 'service' && order.operational_status !== 'cancelled' && order.payment_status !== 'paid' && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleStatusChange('payment', 'paid')}
                  disabled={loading}
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  Отметить оплаченным
                </Button>
              )}

              {/* Send Reminder — hide for service orders */}
              {order.order_type !== 'service' && order.client && (
                <ReminderButton order={order} client={order.client} size="sm" />
              )}

              {/* Create Service button for done orders */}
              {order.operational_status === 'done' && order.order_type !== 'service' && (
                <Button 
                  size="sm"
                  variant="outline"
                  className="w-full border-amber-500/40 text-amber-600 hover:bg-amber-500/10"
                  onClick={() => {
                    onOpenChange(false);
                    onCreateService?.(order);
                  }}
                  data-testid="order-create-service-btn"
                >
                  <Wrench className="mr-2 h-4 w-4" />
                  Создать сервис
                </Button>
              )}
            </div>
          )}

          {/* Service badge for service orders */}
          {order.order_type === 'service' && (
            <div className="space-y-3">
              {/* Service task description — prominent */}
              {order.service_reason && (
                <div className="p-4 rounded-lg border-2 border-amber-500/30 bg-amber-500/5">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Сервисное задание</h4>
                  <p className="text-sm leading-relaxed">{order.service_reason}</p>
                </div>
              )}
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm space-y-1">
                <div className="flex items-center gap-2 font-medium text-amber-600 dark:text-amber-400">
                  <Wrench className="h-4 w-4" />
                  Сервисный выезд
                </div>
                <p className="text-xs text-muted-foreground">
                  {order.fault_type === 'worker_fault' ? 'Вина сотрудника — бесплатно' : 'Вина компании — оплачивается по часам'}
                </p>
                {order.parent_order_id && (
                  <button
                    className="text-xs text-primary flex items-center gap-1 hover:underline"
                    onClick={() => {
                      window.location.href = `/orders?view=${order.parent_order_id}`;
                    }}
                    data-testid="view-parent-order"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Перейти к основному заказу
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Service required indicator on parent order */}
          {order.service_required && order.order_type !== 'service' && (
            <button
              className="w-full p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-left hover:bg-amber-500/20 transition-colors group"
              onClick={async () => {
                try {
                  const res = await api.get(`/orders?order_type=service&limit=50`);
                  const svc = res.data.find(o => o.parent_order_id === order.id);
                  if (svc) {
                    // Закрываем текущую панель и открываем сервис
                    onOpenChange(false);
                    // Используем onViewService callback
                    setTimeout(() => onViewService?.(svc), 300);
                  } else {
                    // Сервис не найден
                  }
                } catch {}
              }}
              data-testid="view-service-order"
            >
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                  По этому заказу создан сервис
                </span>
                <ExternalLink className="h-3 w-3 text-amber-500 ml-auto group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          )}

          {/* Tabbed content: Timeline / Photos / Files */}
          <div className="mt-4 pt-4 border-t">
            <Tabs defaultValue="timeline">
              <TabsList className="grid w-full grid-cols-4 h-8" data-testid="order-detail-tabs">
                <TabsTrigger value="timeline" className="text-xs" data-testid="order-tab-timeline">Активность</TabsTrigger>
                <TabsTrigger value="expenses" className="text-xs" data-testid="order-tab-expenses">Расходы</TabsTrigger>
                <TabsTrigger value="photos" className="text-xs" data-testid="order-tab-photos">Фото</TabsTrigger>
                <TabsTrigger value="files" className="text-xs" data-testid="order-tab-files">Файлы</TabsTrigger>
              </TabsList>
              <TabsContent value="timeline" className="mt-3">
                <OrderTimeline orderId={order.id} canManage={hasRole('admin') || hasRole('manager')} />
              </TabsContent>
              <TabsContent value="expenses" className="mt-3">
                <OrderExpenses orderId={order.id} canManage={hasRole('admin') || hasRole('manager')} />
              </TabsContent>
              <TabsContent value="photos" className="mt-3">
                <OrderPhotosGallery orderId={order.id} />
              </TabsContent>
              <TabsContent value="files" className="mt-3">
                <FilesList ownerType="order" ownerId={order.id} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
